import { http, URL } from './http.service';

const normalizeBannerSettings = (settings = {}) => {
  const bannerImage = settings.bannerImage || settings.banner || null;
  if (!bannerImage) {
    return {
      ...settings,
      bannerImage: null,
      banner: null,
    };
  }

  return {
    ...settings,
    bannerImage,
    banner: bannerImage,
  };
};

const normalizeStoreSettingsResponse = (payload) => {
  if (!payload?.data) return payload;

  return {
    ...payload,
    data: normalizeBannerSettings(payload.data),
  };
};

class StoreSettingsService {
  // Get store settings
  async getStoreSettings(businessUuid) {
    try {
      const response = await http.get(`${URL}/store-settings/${businessUuid}`);
      return normalizeStoreSettingsResponse(response.data);
    } catch (error) {
      console.error('Error fetching store settings:', error);
      
      // Return default settings if not found
      if (error.response?.status === 404) {
        return {
          success: true,
          data: {
            theme: {
              themeName: 'Modern Indigo',
              themeId: '',
              themePreviewImage: '',
              heroEyebrow: '',
              heroTitle: '',
              heroSubtitle: '',
              primaryCtaText: '',
              secondaryCtaText: '',
              fontFamily: 'Inter',
              primaryColor: '#4f46e5',
              secondaryColor: '#10b981',
              backgroundColor: '#ffffff',
              buttonColor: '#4f46e5',
              textColor: '#1f2937',
              headingColor: '#111827',
              borderRadius: '12px',
              buttonStyle: 'rounded',
              cardStyle: 'shadow',
              hideBanner: false
            },
            themeSelection: {
              source: 'business-default',
              updatedAt: new Date().toISOString()
            },
            bannerImage: {
              type: 'image',
              imageUrl: '',
              color: '#4f46e5'
            },
            deliverySlotSettings: {
              enabled: false
            },
            checkoutSettings: {
              cashOnDeliveryEnabled: true
            },
            // NEW: Default call and whatsapp settings
            callAndWhatsappSettings: {
              showCallButton: true,
              showWhatsappButton: true,
              phoneNumber: '',
              whatsappNumber: '',
              whatsappMessage: 'Hi! I am interested in your products.'
            },
            storeDescription: '',
            contactInfo: {},
            socialLinks: {},
            metaTitle: '',
            metaDescription: '',
            metaKeywords: []
          }
        };
      }
      
      throw error;
    }
  }

  // NEW: Get public settings for storefront
  async getPublicStoreSettings(businessUuid) {
    try {
      const response = await http.get(`${URL}/store-settings/${businessUuid}/public-settings`);
      return normalizeStoreSettingsResponse(response.data);
    } catch (error) {
      console.error('Error fetching public store settings:', error);
      return {
        success: true,
        data: {
          theme: {
            themeName: 'Modern Indigo',
            themeId: '',
            themePreviewImage: '',
            fontFamily: 'Inter',
            primaryColor: '#4f46e5',
            secondaryColor: '#10b981',
            backgroundColor: '#ffffff',
            buttonColor: '#4f46e5',
            textColor: '#1f2937',
            headingColor: '#111827',
            borderRadius: '12px',
            buttonStyle: 'rounded',
            cardStyle: 'shadow',
            hideBanner: false
          },
          themeSelection: {
            source: 'business-default',
            updatedAt: new Date().toISOString()
          },
          bannerImage: {
            type: 'image',
            imageUrl: '',
            color: '#4f46e5'
          },
          callAndWhatsappSettings: {
            showCallButton: true,
            showWhatsappButton: true,
            phoneNumber: '',
            whatsappNumber: '',
            whatsappMessage: 'Hi! I am interested in your products.'
          },
          storeDescription: ''
        }
      };
    }
  }

  // Update theme settings
  async updateTheme(businessUuid, themeData) {
    try {
      const response = await http.put(`${URL}/store-settings/${businessUuid}/theme`, themeData);
      return response.data;
    } catch (error) {
      console.error('Error updating theme:', error);
      throw error;
    }
  }

  // Upload banner image
  async uploadBanner(businessUuid, file) {
    try {
      const formData = new FormData();
      formData.append('bannerImage', file);
      
      const response = await http.post(`${URL}/store-settings/${businessUuid}/bannerImage/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading banner:', error);
      throw error;
    }
  }

  // Update banner settings (color/gradient)
  async updateBanner(businessUuid, bannerData) {
    try {
      const response = await http.put(`${URL}/store-settings/${businessUuid}/bannerImage`, bannerData);
      return normalizeStoreSettingsResponse(response.data);
    } catch (error) {
      console.error('Error updating banner:', error);
      throw error;
    }
  }

  // Update store description
  async updateDescription(businessUuid, description) {
    try {
      const response = await http.put(`${URL}/store-settings/${businessUuid}/description`, {
        description
      });
      return response.data;
    } catch (error) {
      console.error('Error updating description:', error);
      throw error;
    }
  }

  // Update all settings
  async updateAllSettings(businessUuid, settings) {
    try {
      const normalizedSettings = { ...settings };

      if (normalizedSettings.banner && !normalizedSettings.bannerImage) {
        normalizedSettings.bannerImage = normalizedSettings.banner;
      }

      delete normalizedSettings.banner;

      const response = await http.put(`${URL}/store-settings/${businessUuid}`, normalizedSettings);
      return normalizeStoreSettingsResponse(response.data);
    } catch (error) {
      console.error('Error updating all settings:', error);
      throw error;
    }
  }

  // Get layout settings
  async getLayoutSettings(businessUuid) {
    try {
      const response = await http.get(`${URL}/store-settings/${businessUuid}/layout`);
      return response.data;
    } catch (error) {
      console.error('Error fetching layout settings:', error);
      throw error;
    }
  }
  
  // Update layout settings
  async updateLayout(businessUuid, layoutData) {
    try {
      const response = await http.put(`${URL}/store-settings/${businessUuid}/layout`, layoutData);
      return response.data;
    } catch (error) {
      console.error('Error updating layout:', error);
      throw error;
    }
  }
  
  // Update specific layout setting
  async updateLayoutSetting(businessUuid, key, value) {
    try {
      const response = await http.put(`${URL}/store-settings/${businessUuid}/layout/setting`, {
        key,
        value
      });
      return response.data;
    } catch (error) {
      console.error('Error updating layout setting:', error);
      throw error;
    }
  }
  
  // Toggle landing page visibility
  async toggleLandingPage(businessUuid, showLanding) {
    try {
      const response = await http.put(`${URL}/store-settings/${businessUuid}/layout/setting`, {
        key: 'showLanding',
        value: showLanding
      });
      return response.data;
    } catch (error) {
      console.error('Error toggling landing page:', error);
      throw error;
    }
  }

  async getMetaConnectUrl(businessUuid) {
    try {
      const response = await http.get(`${URL}/store-settings/${businessUuid}/meta/connect-url`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Meta connect URL:', error);
      throw error;
    }
  }

  async getMetaStatus(businessUuid) {
    try {
      const response = await http.get(`${URL}/store-settings/${businessUuid}/meta/status`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Meta status:', error);
      throw error;
    }
  }

  async getMetaPublicConfig(businessUuid) {
    try {
      const response = await http.get(`${URL}/store-settings/${businessUuid}/meta/public-config`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Meta public config:', error);
      throw error;
    }
  }

  async updateMetaConfig(businessUuid, payload) {
    try {
      const response = await http.put(`${URL}/store-settings/${businessUuid}/meta/config`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating Meta config:', error);
      throw error;
    }
  }

  async getMetaMarketingConfig(businessUuid) {
    try {
      const response = await http.get(`${URL}/store-settings/${businessUuid}/meta/marketing-config`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Meta marketing config:', error);
      throw error;
    }
  }

  async getMetaCatalogDashboard(businessUuid) {
    try {
      const response = await http.get(`${URL}/store-settings/${businessUuid}/meta/catalog-dashboard`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Meta catalog dashboard:', error);
      throw error;
    }
  }

  async updateMetaMarketingConfig(businessUuid, payload) {
    try {
      const response = await http.put(`${URL}/store-settings/${businessUuid}/meta/marketing-config`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating Meta marketing config:', error);
      throw error;
    }
  }

  async getMetaAdsPerformance(businessUuid, params = {}) {
    try {
      const query = new URLSearchParams(params).toString();
      const suffix = query ? `?${query}` : '';
      const response = await http.get(`${URL}/store-settings/${businessUuid}/meta/ads-performance${suffix}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Meta ads performance:', error);
      throw error;
    }
  }

  async getMetaAnalytics(businessUuid, params = {}) {
    try {
      const query = new URLSearchParams(params).toString();
      const suffix = query ? `?${query}` : '';
      const response = await http.get(`${URL}/store-settings/${businessUuid}/meta/analytics${suffix}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Meta analytics:', error);
      throw error;
    }
  }

  async disconnectMeta(businessUuid) {
    try {
      const response = await http.delete(`${URL}/store-settings/${businessUuid}/meta/disconnect`);
      return response.data;
    } catch (error) {
      console.error('Error disconnecting Meta:', error);
      throw error;
    }
  }

  async removeBanner(businessUuid) {
    try {
      const response = await this.http.delete(`${this.URL}/${businessUuid}/banner`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Custom Theme Layout (Craft.js) Methods
  async saveCustomThemeLayout(businessUuid, layoutData) {
    try {
      const response = await http.post(`${URL}/store-settings/${businessUuid}/custom-layouts`, layoutData);
      return response.data;
    } catch (error) {
      console.error('Error saving custom theme layout:', error);
      throw error;
    }
  }

  async getCustomThemeLayout(businessUuid, themeId = 'custom') {
    try {
      const response = await http.get(`${URL}/store-settings/${businessUuid}/custom-layouts/${themeId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching custom theme layout:', error);
      throw error;
    }
  }

  async getActiveCustomThemeLayout(businessUuid) {
    try {
      const response = await http.get(`${URL}/store-settings/${businessUuid}/custom-layouts/active`);
      return response.data;
    } catch (error) {
      console.error('Error fetching active custom theme layout:', error);
      throw error;
    }
  }

  async activateCustomThemeLayout(businessUuid, themeId = 'custom') {
    try {
      const response = await http.put(`${URL}/store-settings/${businessUuid}/custom-layouts/${themeId}/activate`);
      return response.data;
    } catch (error) {
      console.error('Error activating custom theme layout:', error);
      throw error;
    }
  }
}

export const storeSettingsService = new StoreSettingsService();


